import React from "react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixVertical } from "@phosphor-icons/react";

function SortableItem({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition, position: "relative", zIndex: isDragging ? 10 : undefined };
  const handle = disabled ? null : (
    <button type="button" ref={setActivatorNodeRef} {...attributes} {...listeners} data-testid={`drag-handle-${id}`} title="Drag to reorder"
      className="p-1 -ml-1 rounded text-neutral-300 hover:text-neutral-700 hover:bg-neutral-100 cursor-grab active:cursor-grabbing touch-none">
      <DotsSixVertical size={16} weight="bold" />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? "shadow-lg bg-white rounded-md opacity-95" : ""}>
      {children(handle)}
    </div>
  );
}

export function SortableList({ items, getId, onReorder, disabled = false, renderItem, className = "" }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map(getId);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onReorder(arrayMove(items, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {items.map((item, idx) => (
            <SortableItem key={ids[idx]} id={ids[idx]} disabled={disabled}>
              {(handle) => renderItem(item, idx, handle)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
